import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, Store, MapPin, Clock, MessageCircle, Minus, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCart } from '@/context/CartContext';
import { useSite } from '@/context/SiteContext';

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, formatPrice, clearCart } = useCart();
  const { siteData } = useSite();

  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    observations: ''
  });
  const [errors, setErrors] = useState({});

  const subtotal = getCartTotal();
  const shipping = siteData?.shipping || { flatRate: 5, freeFrom: 50 };
  const shippingCost = deliveryMethod === 'pickup' ? 0 : (subtotal >= shipping.freeFrom ? 0 : shipping.flatRate);
  const total = subtotal + shippingCost;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!deliveryMethod) {
      newErrors.deliveryMethod = 'Por favor, seleciona um método de entrega';
    }

    if (deliveryMethod === 'shipping') {
      if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
      if (!formData.phone.trim()) newErrors.phone = 'Telemóvel é obrigatório';
      if (!formData.address.trim()) newErrors.address = 'Morada é obrigatória';
      if (!formData.postalCode.trim()) newErrors.postalCode = 'Código postal é obrigatório';
      if (!formData.city.trim()) newErrors.city = 'Localidade é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateWhatsAppMessage = () => {
    let message = `Olá! Quero fazer esta encomenda na Festa Encantada:\n\n`;
    
    // Products
    cartItems.forEach(item => {
      message += `- ${item.name} (Qtd ${item.quantity}) — ${formatPrice(item.price * item.quantity)}\n`;
    });
    
    message += `\nSubtotal: ${formatPrice(subtotal)}\n`;
    
    if (deliveryMethod === 'shipping') {
      if (shippingCost > 0) {
        message += `Portes: ${formatPrice(shippingCost)} (Grátis a partir de 50,00 €)\n`;
      } else {
        message += `Portes: Grátis (compras a partir de 50,00 €)\n`;
      }
      message += `Total: ${formatPrice(total)}\n\n`;
      message += `Método: Envio (CTT)\n`;
      message += `Nome: ${formData.name}\n`;
      message += `Telemóvel: ${formData.phone}\n`;
      message += `Morada: ${formData.address}\n`;
      message += `Código Postal: ${formData.postalCode}\n`;
      message += `Localidade: ${formData.city}\n`;
    } else {
      message += `Total: ${formatPrice(total)}\n\n`;
      message += `Método: Levantamento\n`;
      message += `Local: ${siteData?.pickup?.name} — ${siteData?.pickup?.fullAddress}\n`;
      message += `Nota: encomenda pronta dentro de 1 hora (confirmar via WhatsApp).\n`;
    }
    
    if (formData.observations.trim()) {
      message += `Observações: ${formData.observations}\n`;
    }

    return encodeURIComponent(message);
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    
    const message = generateWhatsAppMessage();
    const whatsappNumber = siteData?.contact?.whatsappNumber || '351967472503';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;
    
    window.open(whatsappUrl, '_blank');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="h-12 w-12 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4">Carrinho Vazio</h1>
            <p className="text-muted-foreground mb-8">
              O teu carrinho está vazio. Adiciona alguns produtos para fazer a tua encomenda!
            </p>
            <Button asChild className="btn-festa">
              <Link to="/loja">Ver Produtos</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-muted/30 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Início</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Finalizar Encomenda</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          className="mb-6 -ml-2" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-8">
          Finalizar Encomenda
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items & Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Cart Items */}
            <div className="bg-card rounded-2xl p-6 shadow-festa-sm">
              <h2 className="font-bold text-foreground mb-4">Produtos ({cartItems.length})</h2>
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4 p-3 bg-muted/30 rounded-xl">
                    <Link to={`/produto/${item.id}`} className="flex-shrink-0">
                      <img
                        src={item.images?.[0] || 'https://via.placeholder.com/80x80'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/produto/${item.id}`}
                        className="font-medium text-foreground text-sm line-clamp-2 hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-full"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center font-medium text-sm">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-full"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 ml-auto text-muted-foreground hover:text-destructive"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-muted-foreground">
                          {formatPrice(item.price)} × {item.quantity}
                        </span>
                        <span className="font-semibold text-primary">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Method */}
            <div className="bg-card rounded-2xl p-6 shadow-festa-sm">
              <h2 className="font-bold text-foreground mb-4">Método de Entrega</h2>
              
              {errors.deliveryMethod && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errors.deliveryMethod}</AlertDescription>
                </Alert>
              )}

              <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod} className="space-y-4">
                {/* Shipping Option */}
                <div 
                  className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all ${
                    deliveryMethod === 'shipping' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setDeliveryMethod('shipping')}
                >
                  <div className="flex items-start gap-4">
                    <RadioGroupItem value="shipping" id="shipping" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="shipping" className="flex items-center gap-2 font-semibold cursor-pointer">
                        <Truck className="h-5 w-5 text-secondary" />
                        Envio (CTT) / Entrega ao domicílio
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {subtotal >= shipping.freeFrom 
                          ? 'Portes grátis (compras a partir de 50,00 €)' 
                          : `Portes: ${formatPrice(shipping.flatRate)} (Grátis a partir de 50,00 €)`
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pickup Option */}
                <div 
                  className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all ${
                    deliveryMethod === 'pickup' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setDeliveryMethod('pickup')}
                >
                  <div className="flex items-start gap-4">
                    <RadioGroupItem value="pickup" id="pickup" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="pickup" className="flex items-center gap-2 font-semibold cursor-pointer">
                        <Store className="h-5 w-5 text-primary" />
                        Levantamento
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">Grátis • Pronto em 1 hora</p>
                    </div>
                  </div>
                </div>
              </RadioGroup>

              {/* Shipping Form */}
              {deliveryMethod === 'shipping' && (
                <div className="mt-6 pt-6 border-t border-border space-y-4">
                  <h3 className="font-semibold text-foreground">Dados de Envio</h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="O teu nome completo"
                        className={errors.name ? 'border-destructive' : ''}
                      />
                      {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telemóvel *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="912 345 678"
                        className={errors.phone ? 'border-destructive' : ''}
                      />
                      {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Morada Completa *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Rua, número, andar..."
                      className={errors.address ? 'border-destructive' : ''}
                    />
                    {errors.address && <p className="text-xs text-destructive">{errors.address}</p>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postalCode">Código Postal *</Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="0000-000"
                        className={errors.postalCode ? 'border-destructive' : ''}
                      />
                      {errors.postalCode && <p className="text-xs text-destructive">{errors.postalCode}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Localidade *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Lisboa"
                        className={errors.city ? 'border-destructive' : ''}
                      />
                      {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="observations">Observações (opcional)</Label>
                    <Textarea
                      id="observations"
                      name="observations"
                      value={formData.observations}
                      onChange={handleInputChange}
                      placeholder="Alguma nota especial para a encomenda?"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Pickup Info */}
              {deliveryMethod === 'pickup' && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-foreground">{siteData?.pickup?.name}</p>
                        <p className="text-sm text-muted-foreground">{siteData?.pickup?.fullAddress}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-foreground">Pronto em 1 hora</p>
                        <p className="text-sm text-muted-foreground">{siteData?.pickup?.note}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label htmlFor="observations-pickup">Observações (opcional)</Label>
                    <Textarea
                      id="observations-pickup"
                      name="observations"
                      value={formData.observations}
                      onChange={handleInputChange}
                      placeholder="Alguma nota especial para a encomenda?"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-card rounded-2xl p-6 shadow-festa-sm">
              <h2 className="font-bold text-foreground mb-4">Resumo da Encomenda</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Portes</span>
                  {deliveryMethod ? (
                    <span className="font-medium">
                      {shippingCost === 0 ? (
                        <span className="text-success">Grátis</span>
                      ) : (
                        formatPrice(shippingCost)
                      )}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>

                {deliveryMethod === 'shipping' && subtotal < shipping.freeFrom && (
                  <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-lg">
                    Faltam <strong>{formatPrice(shipping.freeFrom - subtotal)}</strong> para portes grátis!
                  </p>
                )}
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between items-center mb-6">
                <span className="font-semibold text-foreground">Total</span>
                <span className="text-2xl font-bold text-primary">{formatPrice(total)}</span>
              </div>

              <Button 
                className="w-full btn-festa py-6 text-base"
                onClick={handleSubmit}
                disabled={!deliveryMethod}
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Finalizar no WhatsApp
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Ao clicar, serás redirecionado para o WhatsApp para confirmar a tua encomenda.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
